import { useAuthReady } from './useAuthReady';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cardAccount,
  ensureCardAccount,
  fetchCard,
  fetchCards,
  fetchCardTransactions,
  fetchTemplates,
  type Card,
  type CardTemplate,
} from '../lib/cards';
import { readCardTransactionsCache, writeCardTransactionsCache } from '../lib/cardTransactionCache';
import { cardTxToTx, type CardTxDto, type Tx } from '../components/wallet/TransactionRow';

/**
 * Poll card balance + tx history while the TMA is open (webhooks only hit the bot).
 * Banana allows 60 balance reads per minute for the whole merchant, so the
 * visible card refreshes every 30s and the API serves a short-lived cache.
 */
export const CARD_LIVE_POLL_MS = 30_000;
const CARD_BALANCE_STALE_MS = 25_000;
const CARD_TX_POLL_MS = 30_000;

// Opening a wallet with many imported cards used to hit Banana for every
// balance and history at once. Keep the UI queries independent but cap the
// browser's provider-backed fan-out.
const CARD_READ_CONCURRENCY = 2;
let cardReadsRunning = 0;
const cardReadQueue: (() => void)[] = [];

async function limitedCardRead<T>(read: () => Promise<T>): Promise<T> {
  if (cardReadsRunning >= CARD_READ_CONCURRENCY) {
    await new Promise<void>((resolve) => cardReadQueue.push(resolve));
  }
  cardReadsRunning += 1;
  try {
    return await read();
  } finally {
    cardReadsRunning -= 1;
    cardReadQueue.shift()?.();
  }
}

export const cardTxQueryOpts = (
  initData: string,
  userId: string | number | null,
  cardId: string,
) => {
  const cached = readCardTransactionsCache(userId, cardId);
  return {
    queryKey: ['card', cardId, 'transactions', userId] as const,
    queryFn: async () => {
      const data = await limitedCardRead(() => fetchCardTransactions(initData, cardId));
      writeCardTransactionsCache(userId, cardId, data);
      return data;
    },
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.updatedAt,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always' as const,
    refetchInterval: CARD_TX_POLL_MS,
  };
};

/** Live balance for one card (polls Banana via our API). */
export function useCardBalance(cardId: string | undefined, enabled = true) {
  const { canFetch, initData, cacheUserId: userId } = useAuthReady();
  const ready = canFetch && !!cardId && enabled;
  return useQuery({
    queryKey: ['card', cardId, 'balance', userId],
    enabled: ready,
    queryFn: () => limitedCardRead(() => fetchCard(initData, cardId!)),
    staleTime: CARD_BALANCE_STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
    // Polling lives in useAllCardBalances. This hook only reads the shared
    // cache — otherwise every card in the home carousel would poll on its own.
    refetchInterval: false,
  });
}

/** Balances for ALL non-pending cards — fetched in parallel and cached per card.
 *  Once a balance is loaded it stays visible (never reverts to undefined) even
 *  while a background refetch is in progress. */
export function useAllCardBalances(cards: Card[], activeCardId?: string): Record<string, number> {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();

  const activeCards = cards.filter((c) => c.status !== 'pending');

  const queries = useQueries({
    queries: activeCards.map((c) => ({
      queryKey: ['card', c.id, 'balance', userId] as const,
      enabled: ready,
      queryFn: () => limitedCardRead(() => fetchCard(initData, c.id)),
      staleTime: CARD_BALANCE_STALE_MS,
      refetchOnWindowFocus: true,
      retry: false,
      // Only the open card is polled. A card whose first read was rate-limited
      // retries until it succeeds; the rest stay on the value already loaded.
      refetchInterval: (query: { state: { status: string } }) => {
        if (c.id === activeCardId) return CARD_LIVE_POLL_MS;
        return query.state.status === 'error' ? 60_000 : false;
      },
    })),
  });

  // Accumulate last-known balances across renders — once we have a value for a
  // card it is never replaced with undefined, so the UI never flashes '—'.
  const lastKnownRef = useRef<Record<string, number>>({});
  activeCards.forEach((c, i) => {
    const b = queries[i]?.data?.balance?.balance;
    if (b != null) lastKnownRef.current[c.id] = b;
  });

  return lastKnownRef.current;
}

/** Per-template registration status. Approval is async, so poll while pending. */
export function useCardAccount(templateId?: number) {
  const { canFetch, initData, cacheUserId: userId } = useAuthReady();
  const ready = canFetch && templateId != null;
  const q = useQuery({
    queryKey: ['card-account', templateId, userId],
    enabled: ready,
    queryFn: () => cardAccount(initData, templateId as number),
    refetchOnMount: 'always',
    // Poll while Banana approval is in flight; REJECTED/APPROVED stop polling
    // (webhook + GET /account also sync terminal states).
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 2500 : false),
  });
  return {
    registered: q.data?.registered ?? false,
    status: q.data?.status ?? 'none',
    rejectionMessage: q.data?.rejectionMessage ?? null,
    loading: q.isLoading,
  };
}

/** The user's issued cards (live from the backend). */
export function useCards() {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const q = useQuery({
    queryKey: ['cards', userId],
    enabled: ready,
    queryFn: () => fetchCards(initData),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    // Poll while issuing, and while the user has live cards (catches 3DS / wallet
    // OTP webhooks that land while the TMA is already open — bot pushes instantly).
    refetchInterval: (query) => {
      const cards = query.state.data?.cards ?? [];
      if (cards.some((c) => c.status === 'pending')) return 5000;
      if (cards.some((c) => c.status === 'active')) return 30_000;
      return false;
    },
  });
  return {
    cards: (q.data?.cards ?? []) as Card[],
    withdrawLimits: q.data?.withdrawLimits ?? null,
    loading: q.isLoading,
    error: q.isError,
  };
}

/** Issuable card products (the offered templates with live fees/limits). */
export function useCardTemplates(enabled = true) {
  const { canFetch, initData } = useAuthReady();
  const ready = canFetch && enabled;
  const q = useQuery({
    queryKey: ['card-templates'],
    enabled: ready,
    staleTime: 5 * 60_000,
    queryFn: () => fetchTemplates(initData),
  });
  return {
    templates: (q.data?.templates ?? []) as CardTemplate[],
    treasury: q.data?.treasury ?? { eth: null, trx: null },
    loading: q.isLoading,
    error: q.isError,
  };
}

/**
 * On app launch, ensure every offered template has an in-flight Banana user.
 * The API is idempotent for an existing account and does not recreate rejected
 * registrations; approval continues asynchronously via webhook/provider status.
 * Templates that require a user-supplied email are skipped (collected at issue).
 */
export function useWarmCardAccounts() {
  const { canFetch, initData } = useAuthReady();
  const { templates } = useCardTemplates();
  const qc = useQueryClient();
  const warmedRef = useRef(new Set<number>());

  useEffect(() => {
    if (!canFetch || templates.length === 0) return;
    const pending = templates.filter(
      (tpl) =>
        !tpl.requiresEmail &&
        !tpl.poolAvailable &&
        !warmedRef.current.has(tpl.templateId),
    );
    if (pending.length === 0) return;
    pending.forEach((tpl) => warmedRef.current.add(tpl.templateId));

    // Keep provider calls sequential to avoid a registration burst on launch.
    void (async () => {
      for (const tpl of pending) {
        try {
          await ensureCardAccount(initData, tpl.templateId);
          await qc.invalidateQueries({ queryKey: ['card-account', tpl.templateId] });
        } catch {
          // Best-effort warm-up only. The explicit issue flow retries and shows
          // the provider error to the user.
        }
      }
    })();
  }, [canFetch, initData, qc, templates]);
}

/** All card transactions across every issued card, merged and sorted newest-first.
 *  Only fetches for active cards (skips pending). Used by the general history screen. */
export function useCardTransactions(): { txs: Tx[]; loading: boolean } {
  const { t, i18n } = useTranslation();
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();

  const cardsQ = useQuery({
    queryKey: ['cards', userId],
    enabled: ready,
    queryFn: () => fetchCards(initData),
    refetchOnMount: 'always',
  });
  const activeCards = ((cardsQ.data?.cards ?? []) as Card[]).filter(
    (c) => c.status !== 'pending',
  );

  const txQueries = useQueries({
    queries: activeCards.map((c) => ({
      ...cardTxQueryOpts(initData, userId, c.id),
      enabled: ready,
    })),
  });

  const loading = cardsQ.isLoading || txQueries.some((q) => q.isLoading);

  const txs: Tx[] = txQueries
    .flatMap((q) => (q.data?.transactions ?? []) as CardTxDto[])
    .map((tx) => cardTxToTx(tx, i18n.language, t))
    .sort((a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0));

  return { txs, loading };
}
